import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Ship } from 'lucide-react';
import { useArtifacts } from '../hooks/useArtifacts';
import { CatalogCard } from '../components/ArtifactCards';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';

export default function PreviousResults() {
  const { artifacts, loading } = useArtifacts();
  const past = artifacts
    .filter((a) => a.status === 'past' || a.status === 'record_sale')
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  return (
    <div className="min-h-screen bg-luxury-black text-white" data-testid="past-page">
      <Seo
        path="/past"
        title="Previous Auction Results | Lunar Legacies Space Memorabilia"
        description="Realized prices from past Lunar Legacies auctions — a documented record of authentic space memorabilia sold since 2009, including flown artifacts, astronaut autographs and mission hardware."
      />
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-b border-white/5 bg-gradient-to-b from-luxury-charcoal to-luxury-black">
        <div className="max-w-6xl mx-auto text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-luxury-gold font-semibold flex items-center justify-center gap-2 mb-4">
            <Trophy size={14} /> Previous Results
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif-luxury text-white tracking-tight mb-5">Previous Auction Results</h1>
          <p className="max-w-2xl mx-auto text-sm md:text-base text-luxury-muted leading-relaxed font-sans-luxury">
            A selection of items sold through Lunar Legacies since our dedicated auctions began in 2009. Hammer results are shown as realized at auction — a documented record of the pieces and prices that have passed through our hands, and a measure of the trust collectors place in our research and provenance.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-luxury-gold animate-pulse font-serif-luxury tracking-widest">LOADING RESULTS...</div>
          ) : past.length === 0 ? (
            <div className="text-center py-16 text-luxury-muted border border-white/5 bg-luxury-charcoal font-sans-luxury text-sm">
              Past auction results are available on request.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {past.map((art) => (
                <CatalogCard key={art.id} art={art} variant="past" />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-14 px-6 md:px-12 lg:px-24 border-t border-white/5 bg-luxury-charcoal">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-luxury-muted font-sans-luxury leading-relaxed mb-6">
            Have a piece with a story like these? We{"'"}d be glad to review it.
          </p>
          <Link to="/consign" className="btn-gold font-sans-luxury text-[11px] uppercase tracking-[0.2em] font-semibold px-9 py-4 rounded-none inline-flex items-center justify-center gap-2" data-testid="past-cta-consign">
            <Ship size={14} /> Consign Your Collection
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
